import React, { ComponentType, FC, Fragment, ReactNode, useState } from 'react';
import {
  Box,
  BoxProps,
  CircularProgress,
  Divider,
  Drawer,
  Fade,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { ChevronLeftOutlined, ChevronRightOutlined } from '@mui/icons-material';
import { Img } from 'react-image';
import { Route, RouteProps, useParams, useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';

import logo from 'url:../assets/images/CC_LP_Logo-Icon_2x.png';
import Breadcrumbs, { BreadcrumProps } from '../components/Breadcrumbs';
import SEO, { SEOProps } from '../components/SEO';
import useAxios from '../hooks/useAxios';
import { Footer } from '../layout/Footer';
import MarketingHeader from '../layout/Marketing/MarketingHeader';
import Sidebar from '../layout/Sidebar';

export const drawerWidth = 240;

type LayoutProps = {
  loading?: boolean;
  Toolbar?: React.ReactNode;
  Header?: any;
  Footer?: any;
  noAppbar?: boolean;
  AdditionalLinks?: () => any;
  seo?: SEOProps;
  breadcrumbs?: BreadcrumProps[];
  LoadingSpinner?: ReactNode;
  bgcolor?: string;
} & BoxProps;

const Layout: React.FC<LayoutProps> = ({
  children,
  loading,
  AdditionalLinks,
  Header,
  Footer: FooterComponent,
  noAppbar: noAppBar = false,
  LoadingSpinner = <CircularProgress />,
  bgcolor,
  padding = [1, 3],
  minHeight = '100vh',
  ...props
}) => {
  const navigate = useNavigate();
  const { classes, cx } = useStyles({ bgcolor: bgcolor });
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const goHome = () => navigate('/');

  const handleToggleDrawer = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <Fragment>
      <Box className={classes.root} {...props}>
        <SEO {...props.seo} />
        <Drawer
          variant="permanent"
          className={cx(classes.drawer, {
            [classes.drawerOpen]: sidebarOpen,
            [classes.drawerClose]: !sidebarOpen,
          })}
          classes={{
            paper: cx({
              [classes.drawerOpen]: sidebarOpen,
              [classes.drawerClose]: !sidebarOpen,
            }),
          }}
        >
          <List
            style={{
              height: '100vh',
              maxHeight: '95vh',
              overflowY: 'auto',
              paddingBottom: 3,
              overflowX: 'hidden',
            }}
          >
            <ListItemButton onClick={goHome}>
              <ListItemIcon>
                <Img src={logo} width={30} alt="logo" />
              </ListItemIcon>
              <ListItemText>
                <strong>LP Exchange</strong>
              </ListItemText>
            </ListItemButton>

            <Divider />

            <Sidebar sidebarOpen={sidebarOpen} />
          </List>
          <Divider />
          <ListItemButton onClick={handleToggleDrawer} disableRipple>
            <ListItemIcon>
              {sidebarOpen ? <ChevronRightOutlined /> : <ChevronLeftOutlined />}
            </ListItemIcon>
          </ListItemButton>
        </Drawer>

        <Box className={classes.content} {...props}>
          <div>{Header}</div>
          {props.breadcrumbs && <Breadcrumbs links={props.breadcrumbs} />}

          <Fade in={loading} unmountOnExit mountOnEnter>
            <Box
              width="100%"
              height="100vh"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              {LoadingSpinner}
            </Box>
          </Fade>

          {!loading && children}
          <div>{FooterComponent}</div>
        </Box>
      </Box>
    </Fragment>
  );
};

export default Layout;

const useStyles = makeStyles<{ bgcolor?: string }>()((theme: Theme, props) => 
  ({
    root: {
      display: 'flex',
      '& img': { margin: 0, padding: 0 },
    },
    appBar: {
      zIndex: theme.zIndex.drawer + 1,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      display: 'flex',
      justifyContent: 'space-between',
    },
    appBarShift: {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    menuButton: {
      marginRight: 36,
      marginLeft: 36,
    },
    hide: {
      display: 'none',
    },
    drawer: {
      width: drawerWidth,
      flexShrink: 0,
      whiteSpace: 'nowrap',
      '& .MuiDivider-root': {
        background: 'none',
      },
    },
    drawerOpen: {
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      overflowX: 'hidden',
    },
    drawerClose: {
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      overflowX: 'hidden',
      width: `calc(${theme.spacing(7)} + 1px)`,
      [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(9)} + 1px)`,
      },
    },
    account: {
      flex: '1 1 auto',
    },
    toolbar: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(0, 1),
    },
    content: {
      width: '100%',
      flexGrow: 1,
      backgroundColor: theme.colors[props?.bgcolor] ? theme.colors[props.bgcolor] : props.bgcolor,
    },
    title: {
      flexGrow: 1,
    },
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
    },
    nested: {
      paddingLeft: theme.spacing(4),
    },
  })
);

export type RouteLayoutProps = RouteProps & {
  component: ComponentType<any>;
  seo?: SEOProps;
  path: string;
};

export const RouteLayout: FC<RouteLayoutProps> = ({ component: Component, seo, ...props }) => (
  <Route
    {...props}
    element={
      <Layout padding={0} seo={seo}>
        <Component />
      </Layout>
    }
  />
);

export const RouteMarketingLayout: FC<RouteLayoutProps> = ({
  component: Component,
  seo,
  ...props
}) => (
  <Route
    {...props}
    element={
      <Box minHeight="100vh" display="flex" flexDirection="column">
        <SEO {...seo} />
        <MarketingHeader />
        <Box flex="1">
          <Component />
        </Box>
        <Footer />
      </Box>
    }
  />
);

interface ComputedProps<T = {}> {
  computedMatch?: {
    params: T;
  };
}

export const RouteLPLayout: FC<RouteLayoutProps> = ({
  component: Component,
  seo,
  ...props
}) => {
  const ElementWrapper: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [{ data: relation, loading, error }] = useAxios<CURelation>(
      `/marketplace/offering/${id}/culprelation`,
      {
        useCache: false,
      }
    );

    if (error) {
      navigate(-1);
      return null;
    }

    const seoProps = id && !loading ? { ...seo, title: `${id}` } : seo;
    return (
      <Layout padding={0} seo={seoProps}>
        <Component {...relation} />
      </Layout>
    );
  }
  
  return <Route {...props} element={<ElementWrapper />} />;
};

export interface CURelation {
  indicated?: boolean;
  committed?: boolean;
  committmentaccepted?: boolean;
}
